import { WORK_LOCATION, WORK_LOCATION_SHORT } from 'constants/worklog';
import { APPROVED, REQUESTED } from 'constants/attendanceStatus';
import { MY_TEAM_ATTENDANCE_FILTERS } from 'constants/myTeam';

import type { TeamAttendanceToday, TeamGroup, TeamMember } from '../types';

export type AttendanceFilter = (typeof MY_TEAM_ATTENDANCE_FILTERS)[keyof typeof MY_TEAM_ATTENDANCE_FILTERS];

/**
 * Today's-attendance category predicates.
 *
 * They are deliberately INDEPENDENT rather than a single-bucket classifier: a
 * record carrying both a leave and a worklog satisfies both the leave and the
 * matching location category, so the four counts may sum to more than the team
 * size. See TSD S-0002.01.
 */
const QUALIFYING_LEAVE_STATUSES: string[] = [REQUESTED, APPROVED];

/**
 * today.leave is not owned by this repo — the backend spreads `today` through
 * opaquely from the external leave service, so the field may arrive as a bare
 * boolean, as an object with a status, or as an object without one. A status, if
 * present, must be requested or approved; absent a status the leave itself
 * qualifies.
 */
export const isOnLeaveToday = (today?: TeamAttendanceToday): boolean => {
  const { leave } = today ?? {};

  if (!leave) {
    return false;
  }

  const status = typeof leave === 'object' ? leave.status : undefined;

  return status === undefined || QUALIFYING_LEAVE_STATUSES.includes(status);
};

/**
 * Both spellings are live: today.worklog.location uses the long form
 * (OFFICE/HOME) while roster.data[].location uses the short form (WFO/WFH), and
 * both reach these predicates.
 *
 * NOTE: these are NOT a restatement of getAttendanceStatusMeta, which drives the
 * row's status icon. That one treats any non-home worklog as office and ignores
 * leave status entirely. These follow the filter spec instead, so the two can
 * disagree on malformed data — see the uncategorised-worklog note below.
 */
export const isWfoToday = (today?: TeamAttendanceToday): boolean => {
  const { location } = today?.worklog ?? {};

  return location === WORK_LOCATION.OFFICE || location === WORK_LOCATION_SHORT.OFFICE;
};

export const isWfhToday = (today?: TeamAttendanceToday): boolean => {
  const { location } = today?.worklog ?? {};

  return location === WORK_LOCATION.HOME || location === WORK_LOCATION_SHORT.HOME;
};

/**
 * Pending means attendance is owed and missing. A weekend or a public holiday is
 * not owed, so neither counts as pending — consistent with how
 * getAttendanceStatusMeta already treats those days.
 */
export const isPendingToday = (today?: TeamAttendanceToday): boolean => {
  // isWeekend is typed as a number but sibling shapes type it as a boolean, so
  // accept either form rather than depending on which one the payload sends.
  if (Boolean(today?.isWeekend) || today?.holiday?.id != null) {
    return false;
  }

  return !isOnLeaveToday(today) && !today?.worklog;
};

const PREDICATE_BY_FILTER: Record<AttendanceFilter, (today?: TeamAttendanceToday) => boolean> = {
  [MY_TEAM_ATTENDANCE_FILTERS.MISSING]: isPendingToday,
  [MY_TEAM_ATTENDANCE_FILTERS.WFO]: isWfoToday,
  [MY_TEAM_ATTENDANCE_FILTERS.WFH]: isWfhToday,
  [MY_TEAM_ATTENDANCE_FILTERS.LEAVE]: isOnLeaveToday,
};

const ALL_FILTERS = Object.values(MY_TEAM_ATTENDANCE_FILTERS);

/**
 * Counts DISTINCT members per category across every displayed group, so a
 * member reachable through several relation groups contributes once. Mirrors the
 * Set-per-key shape getMyTeamCount already uses for the relation chips.
 */
export const getAttendanceFilterCounts = (groups: TeamGroup[]): Record<AttendanceFilter, number> => {
  const idsByFilter = new Map<AttendanceFilter, Set<number | string>>(
    ALL_FILTERS.map((filter) => [filter, new Set<number | string>()])
  );

  groups.forEach(({ members = [] }) => {
    members.forEach((member) => {
      const today = member.attendance?.today;

      ALL_FILTERS.forEach((filter) => {
        if (PREDICATE_BY_FILTER[filter](today)) {
          idsByFilter.get(filter).add(member.user.id);
        }
      });
    });
  });

  return Object.fromEntries(ALL_FILTERS.map((filter) => [filter, idsByFilter.get(filter).size])) as Record<
    AttendanceFilter,
    number
  >;
};

/** Whether a member is in the given category today. Internal to this module. */
const matchesAttendanceFilter = (filter: AttendanceFilter, member: TeamMember): boolean =>
  PREDICATE_BY_FILTER[filter](member.attendance?.today);

/**
 * Narrows the displayed groups to the selected category.
 *
 * Filtering happens WITHIN each group, so a member reachable through several
 * relation groups is retained under each of them (AC-6) while
 * getAttendanceFilterCounts still counts them once (AC-1). A group left with no
 * members is dropped rather than rendered empty; when every group drops out the
 * caller is left with nothing to render and falls through to its empty state.
 *
 * With no selection the same array is returned by identity, so an unfiltered
 * render does no work.
 */
export const filterGroupMembers = (groups: TeamGroup[], filter: AttendanceFilter | null): TeamGroup[] => {
  if (filter == null) {
    return groups;
  }

  return groups
    .map((group) => ({
      ...group,
      members: (group.members ?? []).filter((member) => matchesAttendanceFilter(filter, member)),
    }))
    .filter(({ members }) => members.length > 0);
};

/**
 * Single-select toggle: clicking an unselected category selects it, clicking the
 * selected one clears the selection. At most one category is ever active.
 */
export const nextAttendanceFilter = (
  current: AttendanceFilter | null,
  clicked: AttendanceFilter
): AttendanceFilter | null => (current === clicked ? null : clicked);
